import React from 'react'

import {
  Box,
  Button,
  Checkbox,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
} from '@chakra-ui/react'

import { InterviewType } from '@pkg/types/interview'

import { ArrowSvg } from '@/views/assets/ArrowSvg'
import { CheckboxIcon } from '@/views/components/CheckboxIcon/CheckboxIcon'
import {
  FilterKey,
  INTERVIEWER_FILTER_KEY,
  JOB_TITLE_FILTER_KEY,
} from '@/views/screens/Home/FilterHeader/const'

import { EventFilter } from './EventFilter'
import { NameFilter } from './NameFilter'

export function FilterHeader({
  nameFilter,
  jobTitleFilter,
  eventFilter,
  interviewerFilter,
  setNameFilter,
  setJobTitleFilter,
  setEventFilter,
  setInterviewerFilter,
  hasTeam,
  jobTitles,
  interviewers,
  selectedTab,
  hasEvents,
}: {
  nameFilter: string
  jobTitleFilter: string
  eventFilter: string
  interviewerFilter: string
  setNameFilter: React.Dispatch<React.SetStateAction<string>>
  setJobTitleFilter: React.Dispatch<React.SetStateAction<string>>
  setEventFilter: React.Dispatch<React.SetStateAction<string>>
  setInterviewerFilter: React.Dispatch<React.SetStateAction<string>>
  hasTeam: boolean
  jobTitles?: string[]
  interviewers?: string[]
  selectedTab: InterviewType
  hasEvents?: boolean
}) {
  function handleFilterChange(
    filterStorageKey: FilterKey,
    setFilter: React.Dispatch<React.SetStateAction<string>>,
    value: string,
  ) {
    setFilter(value)
    const filterKey =
      filterStorageKey + (selectedTab === 'INTAKE' ? '-intake' : '')
    localStorage.setItem(filterKey, value)
  }

  return (
    <Box
      display={'flex'}
      className={
        'px-3 py-2.5 md:pt-0 mb-2 items-center justify-between min-w-0'
      }
    >
      <Box
        minW={selectedTab === 'INTAKE' ? '210px' : '190px'}
        w={selectedTab === 'INTAKE' ? '290px' : '260px'}
        display={'flex'}
        alignItems={'center'}
        flexShrink={1}
      >
        <Box w={'2.25rem'} flexShrink={0} />
        <NameFilter
          nameFilter={nameFilter}
          setNameFilter={setNameFilter}
          selectedTab={selectedTab}
        />
      </Box>
      <Box
        minW={'120px'}
        w={'190px'}
        fontWeight={'600'}
        display={{
          base: 'none',
          lg: 'block',
        }}
        flexShrink={1}
      >
        <JobTitleSelect
          jobTitles={jobTitles}
          jobTitleFilter={jobTitleFilter}
          onChange={(jobTitle) => {
            handleFilterChange(
              JOB_TITLE_FILTER_KEY,
              setJobTitleFilter,
              jobTitle,
            )
          }}
        />
      </Box>
      {hasEvents && (
        <Box
          minW={'120px'}
          w={'210px'}
          fontWeight={'600'}
          display={{
            base: 'none',
            lg: 'flex',
          }}
          flexShrink={1}
          alignItems={'center'}
        >
          <EventFilter
            eventFilter={eventFilter}
            setEventFilter={setEventFilter}
            selectedTab={selectedTab}
          />
        </Box>
      )}
      {/* Just take space for status column, we don't need to show name */}
      <Box
        display={{
          base: 'none',
          xl: selectedTab === 'INTAKE' ? 'none' : 'block',
        }}
        minW={'110px'}
        w={'160px'}
        flexShrink={1}
        fontWeight={'600'}
      ></Box>
      {hasTeam ? (
        <Box
          fontWeight={'600'}
          display={{
            base: 'none',
            md: 'flex',
          }}
          minW={'120px'}
          w={'210px'}
          flexShrink={1}
          alignItems={'center'}
        >
          <InterviewerSelect
            interviewers={interviewers}
            interviewerFilter={interviewerFilter}
            onChange={(interviewer) => {
              handleFilterChange(
                INTERVIEWER_FILTER_KEY,
                setInterviewerFilter,
                interviewer,
              )
            }}
          />
        </Box>
      ) : null}
      <Box
        minW={'100px'}
        w={'140px'}
        fontWeight={'600'}
        display={{
          base: 'none',
          sm: 'block',
        }}
        flexShrink={1}
      >
        Date
      </Box>
      <Box w={'2.5rem'} flexShrink={0} />
    </Box>
  )
}

function JobTitleSelect({
  jobTitles,
  jobTitleFilter,
  onChange,
}: {
  jobTitles?: string[]
  jobTitleFilter: string
  onChange?: (jobTitle: string) => void
}) {
  return (
    <Menu>
      {({ isOpen }) => (
        <>
          <MenuButton
            rounded={'1.25rem'}
            as={Button}
            rightIcon={<ArrowSvg ml={4} verticalAlign={'middle'} />}
            variant={'unstyled'}
            display={'flex'}
            alignItems={'center'}
            px={4}
            bgColor={isOpen || jobTitleFilter ? '#E0E0E0' : ''}
            _hover={{
              backgroundColor: '#E0E0E0',
            }}
            ml={-4}
          >
            <Box
              maxW={'150px'}
              textOverflow={'ellipsis'}
              overflow={'hidden'}
              whiteSpace={'nowrap'}
              display={'inline-block'}
              verticalAlign={'middle'}
            >
              {jobTitleFilter || 'Role'}
            </Box>
          </MenuButton>
          <MenuList w={'fit-content'}>
            <MenuItem
              onClick={() => {
                onChange?.('')
              }}
              fontWeight={'normal'}
            >
              <Checkbox
                pointerEvents={'none'}
                variant={'ghost'}
                isChecked={jobTitleFilter === ''}
                icon={<CheckboxIcon />}
              >
                Show all
              </Checkbox>
            </MenuItem>
            <MenuDivider />
            {jobTitles?.map((jobTitle) => (
              <MenuItem
                key={jobTitle}
                onClick={() => {
                  onChange?.(jobTitle)
                }}
                fontWeight={'normal'}
              >
                <Checkbox
                  pointerEvents={'none'}
                  variant={'ghost'}
                  isChecked={jobTitleFilter === jobTitle}
                  icon={<CheckboxIcon />}
                >
                  {jobTitle}
                </Checkbox>
              </MenuItem>
            ))}
          </MenuList>
        </>
      )}
    </Menu>
  )
}

function InterviewerSelect({
  interviewers,
  interviewerFilter,
  onChange,
}: {
  interviewers?: string[]
  interviewerFilter: string
  onChange?: (interviewer: string) => void
}) {
  return (
    <Menu>
      {({ isOpen }) => (
        <>
          <MenuButton
            rounded={'1.25rem'}
            as={Button}
            rightIcon={<ArrowSvg ml={4} verticalAlign={'middle'} />}
            variant={'unstyled'}
            display={'flex'}
            alignItems={'center'}
            px={4}
            bgColor={isOpen || interviewerFilter ? '#E0E0E0' : ''}
            _hover={{
              backgroundColor: '#E0E0E0',
            }}
            ml={-4}
          >
            <Box
              maxW={'100px'}
              textOverflow={'ellipsis'}
              overflow={'hidden'}
              whiteSpace={'nowrap'}
              display={'inline-block'}
              verticalAlign={'middle'}
            >
              {interviewerFilter || 'Interviewer'}
            </Box>
          </MenuButton>
          <MenuList w={'fit-content'}>
            <MenuItem
              onClick={() => {
                onChange?.('')
              }}
              fontWeight={'normal'}
            >
              <Checkbox
                pointerEvents={'none'}
                variant={'ghost'}
                isChecked={interviewerFilter === ''}
                icon={<CheckboxIcon />}
              >
                Show all
              </Checkbox>
            </MenuItem>
            <MenuDivider />
            {interviewers?.map((interviewer) => (
              <MenuItem
                key={interviewer}
                onClick={() => {
                  onChange?.(interviewer)
                }}
                fontWeight={'normal'}
              >
                <Checkbox
                  pointerEvents={'none'}
                  variant={'ghost'}
                  isChecked={interviewerFilter === interviewer}
                  icon={<CheckboxIcon />}
                >
                  {interviewer}
                </Checkbox>
              </MenuItem>
            ))}
          </MenuList>
        </>
      )}
    </Menu>
  )
}
